import React from "react";
import styled from "styled-components";
import { BarChart2, ExternalLink, Globe } from "react-feather";
import { Banner, Spinner, Table as BaseTable } from "@cinesys/ui/components";
import SpotterHeader from "../SpotterHeader";
import { SPOTTER_STRINGS } from "../strings";
import { isSpotterAccessDeniedError } from "../accessError";
import { SPOTTER_INTERFACE_ID } from "../config";
import { useInterfaceAccess } from "../../../hooks/useInterfaceAccess";
import InterfaceAccessNotice from "../../../components/InterfaceAccessNotice";
import { DelayedSpinner } from "@cinesys/experience-ui/components";
import {
  SpotterUsage,
  SpotterUsageActivityEntry,
  SpotterUsageWeeklyPoint,
  useSpotterUsage,
} from "./service";

/**
 * Spotter Usage tab (Monday 12645252035; round 2, Monday 12650205737, rebuilt to match the full
 * mockup) — the customer's own consumption-only usage dashboard: two footage-hours meters
 * (practice/game) with a season sparkline each, plus a merged Recent Activity table. Built
 * directly against `portal-usage-mock.html` (Drive, rev 2026-07-26b) using this app's own
 * established Spotter patterns (`SpotterHeader` + `Page`/`Wrap`, `Banner`/`Spinner` for load/error
 * states, `--spotter*` theme tokens, the shared `Table` — not a literal port of the mock's own
 * hand-rolled CSS, matching `GamebookScreen.tsx`/`FieldsScreen.tsx`'s convention).
 *
 * The mock's own "Dev note — not customer-facing copy" callout is confirmed (Addison, this round)
 * to mean the note itself isn't shown to the customer — NOT that the rest of the mock (title,
 * descriptors, sparkline, Recent Activity) should be trimmed. Still strictly consumption-only:
 * `GET /spotter/usage`'s response never carries cap/%/dollar fields (see the backend's doc
 * comment) — this screen has nothing to accidentally leak.
 *
 * There is no "not configured" vs. "genuinely zero usage" distinction in the response shape
 * itself — both a not-yet-onboarded org and a real org with a fresh season and no usage yet return
 * the same all-zero shape. `period_start`/`period_end` being `null` is the only signal this screen
 * has for "not onboarded yet", so that's what `notConfigured` keys off.
 */

const Page = styled.div`
  min-height: 100%;
  color: var(--spotterInk);
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  font-size: 15px;
  line-height: 1.5;
`;

const Wrap = styled.div`
  padding: 20px 22px 44px;
  max-width: 1000px;
  margin: 0 auto;

  /* Re-alias @cinesys/ui's generic tokens to this interface's palette (customer-frontend
   * CLAUDE.md convention) so the shared Table below picks up Spotter's own look. */
  --dark400: var(--spotterRaised);
  --dark500: var(--spotterLine2);
  --dark600: var(--spotterLine2);
  --dark700: var(--spotterInkFaint);
  --gray400: var(--spotterInk);
  --gray500: var(--spotterInk);
`;

const PageHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin: 6px 0 4px;
  flex-wrap: wrap;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
`;

const Period = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 13px;
  color: var(--spotterInkFaint);
`;

const SubNote = styled.p`
  color: var(--spotterInkFaint);
  font-size: 13px;
  margin: 0 0 22px;
  max-width: 78ch;
`;

const StatusMessage = styled.div`
  padding: 16px 18px;
  border-radius: 10px;
  background: var(--spotterRaised);
  border: 1px solid var(--spotterLine);
  color: var(--spotterInkSoft);
  font-size: 14px;
`;

const Meters = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MeterCard = styled.div<{ $accent: string }>`
  position: relative;
  overflow: hidden;
  background: var(--spotterSurface);
  border: 1px solid var(--spotterLine);
  border-radius: 16px;
  box-shadow: var(--spotterShadow);
  padding: 22px 22px 18px;

  &::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: linear-gradient(90deg, ${(p) => p.$accent}, transparent);
  }
`;

const MeterHead = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
`;

const MeterIcon = styled.span<{ $accent: string }>`
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  flex: none;
  color: ${(p) => p.$accent};
  background: color-mix(in srgb, ${(p) => p.$accent} 14%, transparent);
  border: 1px solid color-mix(in srgb, ${(p) => p.$accent} 45%, transparent);
`;

const MeterTitle = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--spotterInkSoft);
`;

const MeterDescriptor = styled.div`
  color: var(--spotterInkFaint);
  font-size: 12px;
  margin-top: 2px;
`;

const MeterValue = styled.div<{ $accent: string }>`
  display: flex;
  align-items: baseline;
  gap: 9px;
  margin: 20px 0 4px;

  span:first-child {
    font-size: 44px;
    font-weight: 600;
    letter-spacing: -0.03em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    color: ${(p) => p.$accent};
  }

  span:last-child {
    color: var(--spotterInkSoft);
    font-size: 14px;
  }
`;

const MeterCaption = styled.div`
  color: var(--spotterInkFaint);
  font-size: 13px;
`;

const Spark = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 5px;
  height: 44px;
  margin: 20px 0 6px;
`;

const SparkBar = styled.i<{ $accent: string; $last?: boolean }>`
  flex: 1;
  border-radius: 3px 3px 0 0;
  min-height: 3px;
  opacity: ${(p) => (p.$last ? 1 : 0.85)};
  background: ${(p) =>
    p.$last ? p.$accent : `color-mix(in srgb, ${p.$accent} 55%, transparent)`};
`;

const SparkX = styled.div`
  display: flex;
  justify-content: space-between;
  color: var(--spotterInkFaint);
  font-size: 11px;
  font-family: ui-monospace, Menlo, monospace;
  margin-top: 2px;
`;

const Lower = styled.div`
  margin-top: 20px;
`;

const Panel = styled.div`
  background: var(--spotterSurface);
  border: 1px solid var(--spotterLine);
  border-radius: 14px;
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom: 1px solid var(--spotterLine);
`;

const PanelLabel = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--spotterInkFaint);
`;

const PanelCount = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  color: var(--spotterInkFaint);
`;

const EmptyActivity = styled.div`
  padding: 18px 20px;
  color: var(--spotterInkFaint);
  font-size: 13px;
`;

/** Border-below header instead of the base `Table`'s filled-background stripe + hard drop-shadow
 *  — same treatment `@cinesys/ui`'s `ThemedTable` applies for apps/frontend admin screens,
 *  reproduced locally (rather than importing `ThemedTable` itself) since its border color is
 *  tuned to that app's own palette, not this one's `--spotter*` tokens. */
const Table = styled(BaseTable)`
  > header {
    background-color: transparent;
    border-bottom: 1px solid var(--spotterLine);
    box-shadow: none;
  }
` as typeof BaseTable;

/** Wraps the category `Tag` so IT hugs its own text width — `Tag` is the direct grid-column
 *  child otherwise, and grid items stretch to fill their column (`justify-self: stretch`) by
 *  default, which turns the pill into a full-width bar instead of a compact badge. */
const TypeCell = styled.div`
  display: flex;
`;

const AssetLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--spotterAccent);
  font-size: 13px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const When = styled.span`
  color: var(--spotterInkSoft);
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12.5px;
`;

const Duration = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-variant-numeric: tabular-nums;
  color: var(--spotterInkSoft);
`;

const Tag = styled.span<{ $accent: string }>`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 5px;
  letter-spacing: 0.04em;
  color: ${(p) => p.$accent};
  background: color-mix(in srgb, ${(p) => p.$accent} 14%, transparent);
  border: 1px solid color-mix(in srgb, ${(p) => p.$accent} 45%, transparent);
`;

/** `Intl.DateTimeFormat`'s "Jul 1, 2026" style — matches the mock's own date formatting. */
const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

function formatDate(isoDate: string): string {
  return dateFormatter.format(new Date(`${isoDate}T00:00:00Z`));
}

const activityDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});
const activityTimeFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

function formatActivityWhen(analyzedAt: string): string {
  const d = new Date(analyzedAt);
  return `${activityDateFormatter.format(d)} · ${activityTimeFormatter.format(
    d
  )}`;
}

/** "1h 4m", "44m 12s", or "12s" — durations here are individual clips, not cumulative totals, so
 *  a digital-clock `m:ss` readout reads as more precise than it needs to be for a scan-the-list
 *  table; this matches how the rest of the page states hours (`hoursUnit`, "44.0 hours"). */
function formatClipDuration(durationSec: number): string {
  const total = Math.round(durationSec);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

function Sparkline(props: {
  points: SpotterUsageWeeklyPoint[];
  accent: string;
}) {
  const { points, accent } = props;
  if (points.length === 0) {
    return null;
  }
  const maxHours = Math.max(...points.map((p) => p.hours), 0);
  return (
    <>
      <Spark>
        {points.map((point, i) => (
          <SparkBar
            key={point.week_start}
            $accent={accent}
            $last={i === points.length - 1}
            style={{
              height: `${maxHours > 0 ? (point.hours / maxHours) * 100 : 0}%`,
            }}
          />
        ))}
      </Spark>
      <SparkX>
        <span>
          {points.length > 1
            ? SPOTTER_STRINGS.usage.sparklineWeeksAgo(points.length - 1)
            : SPOTTER_STRINGS.usage.sparklineThisWeek}
        </span>
        <span>{SPOTTER_STRINGS.usage.sparklineThisWeek}</span>
      </SparkX>
    </>
  );
}

function MeterCardView(props: {
  accent: string;
  accentVar: string;
  icon: React.ReactNode;
  title: string;
  descriptor: string;
  caption: string;
  hoursUsed: number;
  weeklyTrend: SpotterUsageWeeklyPoint[];
}) {
  const {
    accent,
    accentVar,
    icon,
    title,
    descriptor,
    caption,
    hoursUsed,
    weeklyTrend,
  } = props;
  return (
    <MeterCard $accent={accentVar}>
      <MeterHead>
        <MeterIcon $accent={accentVar}>{icon}</MeterIcon>
        <div>
          <MeterTitle>{title}</MeterTitle>
          <MeterDescriptor>{descriptor}</MeterDescriptor>
        </div>
      </MeterHead>
      <MeterValue $accent={accentVar}>
        <span>{hoursUsed.toFixed(1)}</span>
        <span>{SPOTTER_STRINGS.usage.hoursUnit}</span>
      </MeterValue>
      <MeterCaption>{caption}</MeterCaption>
      <Sparkline points={weeklyTrend} accent={accent} />
    </MeterCard>
  );
}

function RecentActivity(props: { entries: SpotterUsageActivityEntry[] }) {
  const { entries } = props;
  return (
    <Lower>
      <Panel>
        <PanelHead>
          <PanelLabel>{SPOTTER_STRINGS.usage.recentActivityTitle}</PanelLabel>
          <PanelCount>
            {SPOTTER_STRINGS.usage.recentActivityCount(entries.length)}
          </PanelCount>
        </PanelHead>
        {entries.length === 0 ? (
          <EmptyActivity>
            {SPOTTER_STRINGS.usage.recentActivityEmpty}
          </EmptyActivity>
        ) : (
          <Table
            autoHeight={{}}
            columnGrid
            rowHeight={44}
            columns={[
              { title: SPOTTER_STRINGS.usage.columnAnalyzed, width: "1.6fr" },
              { title: SPOTTER_STRINGS.usage.columnType, width: "1fr" },
              { title: SPOTTER_STRINGS.usage.columnDuration, width: "0.8fr" },
              { title: SPOTTER_STRINGS.usage.columnAsset, width: "0.8fr" },
            ]}
            rows={entries.map((entry, i) => ({
              id: `${entry.asset_id}-${i}`,
              element: () => (
                <>
                  <When>{formatActivityWhen(entry.analyzed_at)}</When>
                  <TypeCell>
                    <Tag
                      $accent={
                        entry.category === "practice"
                          ? "var(--spotterAccent)"
                          : "var(--spotterMerge)"
                      }
                    >
                      {entry.category === "practice"
                        ? SPOTTER_STRINGS.usage.practiceTag
                        : SPOTTER_STRINGS.usage.gameTag}
                    </Tag>
                  </TypeCell>
                  <Duration>{formatClipDuration(entry.duration_sec)}</Duration>
                  <AssetLink
                    href={entry.asset_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {SPOTTER_STRINGS.usage.columnAssetOpen}
                    <ExternalLink size={12} />
                  </AssetLink>
                </>
              ),
            }))}
          />
        )}
      </Panel>
    </Lower>
  );
}

function UsageContent(props: { data: SpotterUsage }) {
  const { data } = props;
  if (!data.period_start || !data.period_end) {
    return <StatusMessage>{SPOTTER_STRINGS.usage.notConfigured}</StatusMessage>;
  }
  return (
    <>
      <PageHead>
        <PageTitle>{SPOTTER_STRINGS.usage.pageTitle}</PageTitle>
        <Period>
          {SPOTTER_STRINGS.usage.periodSince(formatDate(data.period_start))}
        </Period>
      </PageHead>
      <SubNote>{SPOTTER_STRINGS.usage.subNote}</SubNote>
      <Meters>
        <MeterCardView
          accent="var(--spotterAccent)"
          accentVar="var(--spotterAccent)"
          icon={<BarChart2 size={18} />}
          title={SPOTTER_STRINGS.usage.practiceTitle}
          descriptor={SPOTTER_STRINGS.usage.practiceDescriptor}
          caption={SPOTTER_STRINGS.usage.practiceCaption}
          hoursUsed={data.practice.hours_used}
          weeklyTrend={data.practice.weekly_trend}
        />
        <MeterCardView
          accent="var(--spotterMerge)"
          accentVar="var(--spotterMerge)"
          icon={<Globe size={18} />}
          title={SPOTTER_STRINGS.usage.gameTitle}
          descriptor={SPOTTER_STRINGS.usage.gameDescriptor}
          caption={SPOTTER_STRINGS.usage.gameCaption}
          hoursUsed={data.game.hours_used}
          weeklyTrend={data.game.weekly_trend}
        />
      </Meters>
      <RecentActivity entries={data.recent_activity} />
    </>
  );
}

const UsageScreen = () => {
  const access = useInterfaceAccess(SPOTTER_INTERFACE_ID);
  const usageQuery = useSpotterUsage();

  // Org-level entitlement gate — see FieldsScreen.tsx's equivalent doc comment.
  if (access.status !== "granted") {
    return (
      <Page>
        <SpotterHeader />
        {access.status === "denied" && (
          <Wrap>
            <InterfaceAccessNotice reason={access.reason} />
          </Wrap>
        )}
        {access.status === "pending" && <DelayedSpinner padTop />}
      </Page>
    );
  }

  if (usageQuery.isLoading) {
    return (
      <Page>
        <SpotterHeader />
        <Spinner padTop />
      </Page>
    );
  }

  return (
    <Page>
      <SpotterHeader />
      <Wrap>
        {usageQuery.isError ? (
          <Banner
            error={
              isSpotterAccessDeniedError(usageQuery.error)
                ? SPOTTER_STRINGS.usage.accessDeniedError
                : SPOTTER_STRINGS.usage.loadError
            }
            padTop
          />
        ) : usageQuery.data ? (
          <UsageContent data={usageQuery.data} />
        ) : null}
      </Wrap>
    </Page>
  );
};

export default UsageScreen;
